import "./style.scss";
import { useEffect, useMemo, useRef, useState } from "react";
import ArrowSvg from "@/src/svg/CustomUtils/ArrowSvg";
import CloseSvg from "@/src/svg/CustomUtils/CloseSvg";
import { formatDateToApiIso } from "@/src/utils/formatDateToApiIso";

type Props = {
  value: string;
  onChange: (value: string) => void;
  onClose: () => void;
};

type ViewMode = "month" | "year";

const WEEK_DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Agu", "Sep", "Oct", "Nov", "Dec"];

const parseDate = (value: string): Date => {
  if (!value) {
    return new Date();
  }

  const iso = formatDateToApiIso(value);
  if (iso) {
    const [year, month, day] = iso.split("-").map(Number);
    return new Date(year, month - 1, day);
  }

  const timestamp = Date.parse(value);
  if (!Number.isNaN(timestamp)) {
    return new Date(timestamp);
  }

  return new Date();
};

const formatDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const getMonthLabel = (year: number, monthIndex: number): string => {
  return `${MONTHS[monthIndex]} ${year}`;
};

type CalendarDay = {
  date: Date;
  inCurrentMonth: boolean;
};

const CustomDate = ({ value, onChange, onClose }: Props) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const initialDate = useMemo(() => parseDate(value), [value]);

  const [selectedDate, setSelectedDate] = useState<Date | null>(initialDate);
  const [viewYear, setViewYear] = useState<number>(initialDate.getFullYear());
  const [viewMonth, setViewMonth] = useState<number>(initialDate.getMonth());
  const [mode, setMode] = useState<ViewMode>("month");

  const today = useMemo(() => new Date(), []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (rootRef.current && !rootRef.current.contains(target)) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  const yearOptions = useMemo(() => {
    const currentYear = today.getFullYear();
    const start = currentYear;
    const end = 2010;
    const years: number[] = [];

    for (let year = start; year >= end; year -= 1) {
      years.push(year);
    }

    return years;
  }, [today]);

  const weeks = useMemo(() => {
    const firstOfMonth = new Date(viewYear, viewMonth, 1);
    const firstDayOfWeek = (firstOfMonth.getDay() + 6) % 7;
    const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
    const prevMonthDays = new Date(viewYear, viewMonth, 0).getDate();

    const cells: CalendarDay[] = [];

    for (let i = 0; i < firstDayOfWeek; i += 1) {
      const day = prevMonthDays - firstDayOfWeek + 1 + i;
      cells.push({
        date: new Date(viewYear, viewMonth - 1, day),
        inCurrentMonth: false,
      });
    }

    for (let day = 1; day <= daysInMonth; day += 1) {
      cells.push({
        date: new Date(viewYear, viewMonth, day),
        inCurrentMonth: true,
      });
    }

    const totalCells = Math.ceil(cells.length / 7) * 7;
    const nextDaysCount = totalCells - cells.length;

    for (let i = 1; i <= nextDaysCount; i += 1) {
      cells.push({
        date: new Date(viewYear, viewMonth + 1, i),
        inCurrentMonth: false,
      });
    }

    const result: CalendarDay[][] = [];
    for (let i = 0; i < cells.length; i += 7) {
      result.push(cells.slice(i, i + 7));
    }

    return result;
  }, [viewYear, viewMonth]);

  const handleDayClick = (day: CalendarDay) => {
    const newDate = new Date(day.date);
    setSelectedDate(newDate);
    setViewYear(newDate.getFullYear());
    setViewMonth(newDate.getMonth());
    onChange(formatDate(newDate));
    onClose();
  };

  const handlePrevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear((prev) => prev - 1);
    } else {
      setViewMonth((prev) => prev - 1);
    }
  };

  const handleNextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear((prev) => prev + 1);
    } else {
      setViewMonth((prev) => prev + 1);
    }
  };

  const handleSelectYear = (year: number) => {
    setViewYear(year);
  };

  const handleSelectMonth = (monthIndex: number) => {
    setViewMonth(monthIndex);
    setMode("month");
  };

  const isSameDay = (dateA: Date, dateB: Date) => {
    return dateA.getFullYear() === dateB.getFullYear() && dateA.getMonth() === dateB.getMonth() && dateA.getDate() === dateB.getDate();
  };

  const applyToday = (closeAfter: boolean) => {
    const todayDate = new Date();
    setSelectedDate(todayDate);
    setViewYear(todayDate.getFullYear());
    setViewMonth(todayDate.getMonth());
    onChange(formatDate(todayDate));

    if (closeAfter) {
      onClose();
    }
  };

  return (
    <div className="custom-date" ref={rootRef}>
      <div className="custom-date-header">
        <div className="custom-date-header-label" onClick={() => setMode(mode === "month" ? "year" : "month")}>
          <div className="custom-date-header-label-month">
            <p>{getMonthLabel(viewYear, viewMonth)}</p>
          </div>
          <div className="custom-date-header-label-toggle">{mode === "month" ? <ArrowSvg /> : <CloseSvg />}</div>
        </div>
        <div className="custom-date-header-actions">
          <div className="custom-date-header-nav">
            <div className="custom-date-header-nav-btn" onClick={handlePrevMonth}>
              <ArrowSvg />
            </div>
            <div className="custom-date-header-nav-btn" onClick={handleNextMonth}>
              <ArrowSvg />
            </div>
          </div>
          <div className="custom-date-header-close" onClick={onClose} aria-label="Close">
            <CloseSvg />
          </div>
        </div>
      </div>

      <div className="custom-date-content">
        {mode === "month" && (
          <>
            <div className="custom-date-content-weekdays">
              {WEEK_DAYS.map((day) => (
                <div key={day} className="custom-date-content-weekdays-weekday">
                  <p>{day}</p>
                </div>
              ))}
            </div>
            <div className="custom-date-content-days">
              {weeks.map((week, weekIndex) => (
                <div key={weekIndex} className="custom-date-content-days-week">
                  {week.map((day) => {
                    const isSelected = selectedDate ? isSameDay(day.date, selectedDate) : false;
                    // const isToday = isSameDay(day.date, today);

                    const className = [
                      "custom-date-content-days-week-day",
                      isSelected ? "selected" : "",
                      // isToday ? "today" : "",
                      day.inCurrentMonth ? "" : "outside",
                    ]
                      .filter(Boolean)
                      .join(" ");

                    return (
                      <p key={day.date.toISOString()} className={className} onClick={() => handleDayClick(day)}>
                        {day.date.getDate()}
                      </p>
                    );
                  })}
                </div>
              ))}
            </div>
          </>
        )}

        {mode === "year" && (
          <div className="custom-date-content-year-mode">
            <div className="custom-date-content-year-mode-label">
              <p>{viewYear}</p>
            </div>
            <div className="custom-date-content-year-mode-months">
              {MONTHS.map((month, index) => {
                const isActiveMonth = index === viewMonth;

                const className = ["custom-date-content-year-mode-months-month", isActiveMonth ? "current" : ""].filter(Boolean).join(" ");

                return (
                  <p key={month} className={className} onClick={() => handleSelectMonth(index)}>
                    {month}
                  </p>
                );
              })}
            </div>
            <div className="custom-date-content-year-mode-years">
              {yearOptions.map((year) => (
                <p
                  key={year}
                  className={`custom-date-content-year-mode-years-year ${year === viewYear ? "current" : ""}`}
                  onClick={() => handleSelectYear(year)}
                >
                  {year}
                </p>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="custom-date-footer">
        <div className="custom-date-footer-btn" onClick={() => applyToday(true)}>
          <p>Deleted</p>
        </div>
        <div className="custom-date-footer-btn" onClick={() => applyToday(false)}>
          <p>Today</p>
        </div>
      </div>
    </div>
  );
};

export default CustomDate;
